import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getHeraApiBaseUrl } from "@/lib/hera-api-base-url";
import { trackedFetch } from "@/lib/server-request-activity";

type JsonRecord = Record<string, unknown>;
const STREAM_FLUSH_INTERVAL_MS = 200;

/** Completed streams newer than this are served from cache with no network request. */
const STREAM_CACHE_FRESH_TTL_MS = 15 * 60 * 1000;

const streamCompletedCache = new Map<
  string,
  { rows: JsonRecord[]; totalReceived: number; completedAt: number }
>();

/** Bumped when a cached NDJSON stream is invalidated so mounted hooks refetch. */
let streamInvalidationGeneration = 0;

const streamRestarters = new Map<string, () => void>();

function registerStreamRestarter(url: string, restart: () => void) {
  streamRestarters.set(url, restart);
  return () => {
    streamRestarters.delete(url);
  };
}

/**
 * Refetch cached NDJSON streams for matching paths.
 *
 * Cached rows are marked stale rather than dropped so mounted lists keep
 * showing data while the fresh stream loads in the background.
 */
export function invalidateNdjsonStreams(paths: string[]) {
  streamInvalidationGeneration += 1;

  for (const [key, entry] of streamCompletedCache) {
    if (paths.some((path) => key.includes(path))) {
      entry.completedAt = 0;
    }
  }
  for (const [url, refresh] of streamRestarters) {
    if (paths.some((path) => url.includes(path))) {
      refresh();
    }
  }
}

interface UseStreamingNdjsonOptions {
  path: string;
  query?: Record<string, string[] | string | undefined>;
  enabled?: boolean;
}

interface UseStreamingNdjsonResult<T> {
  rows: T[];
  isLoading: boolean;
  isStreaming: boolean;
  error: Error | null;
  totalReceived: number;
  restart: () => void;
}

function _buildUrl(
  path: string,
  query?: Record<string, string[] | string | undefined>,
) {
  const base = getHeraApiBaseUrl().replace(/\/$/, "");
  const url = new URL(`${base}${path}`);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      for (const v of value) {
        if (v !== "") url.searchParams.append(key, v);
      }
    } else if (value !== "") {
      url.searchParams.set(key, value);
    }
  }
  return url.toString();
}

/** Stream newline-delimited JSON and append rows in animation-frame batches. */
export function useStreamingNdjson<T extends JsonRecord>({
  path,
  query,
  enabled = true,
}: UseStreamingNdjsonOptions): UseStreamingNdjsonResult<T> {
  const { getAccessTokenSilently } = useAuth0();
  const [rows, setRows] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [totalReceived, setTotalReceived] = useState(0);
  const [restartVersion, setRestartVersion] = useState(0);
  const bufferedRowsRef = useRef<T[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const url = useMemo(() => _buildUrl(path, query), [path, query]);
  const rowsRef = useRef<T[]>([]);
  rowsRef.current = rows;

  useEffect(() => {
    if (!enabled) return;
    void restartVersion;

    const cached = streamCompletedCache.get(url);
    let staleWhileRevalidate = false;

    if (cached) {
      const age = Date.now() - cached.completedAt;
      setRows(cached.rows as T[]);
      setTotalReceived(cached.totalReceived);
      setError(null);
      if (age <= STREAM_CACHE_FRESH_TTL_MS) {
        setIsLoading(false);
        setIsStreaming(false);
        return;
      }
      staleWhileRevalidate = true;
      setIsLoading(false);
      setIsStreaming(false);
    } else if (rowsRef.current.length > 0) {
      // A global filter changed the URL. Keep the rows already on screen
      // until this stream finishes, instead of blanking the chart.
      staleWhileRevalidate = true;
      setError(null);
      setIsLoading(false);
      setIsStreaming(true);
    } else {
      setRows([]);
      setTotalReceived(0);
      setError(null);
      setIsLoading(true);
      setIsStreaming(true);
    }

    const abortController = new AbortController();
    let localCount = 0;
    let accumulatedRows: T[] = [];
    bufferedRowsRef.current = [];

    const flushBufferedRows = () => {
      flushTimerRef.current = null;
      const pending = bufferedRowsRef.current;
      if (pending.length === 0) return;
      bufferedRowsRef.current = [];
      accumulatedRows = accumulatedRows.concat(pending);
      if (staleWhileRevalidate) {
        return;
      }
      setRows((prev) => prev.concat(pending));
      setTotalReceived(localCount);
    };

    const scheduleFlush = () => {
      if (flushTimerRef.current !== null) return;
      flushTimerRef.current = setTimeout(
        flushBufferedRows,
        STREAM_FLUSH_INTERVAL_MS,
      );
    };

    (async () => {
      let completedWithoutAbort = false;
      try {
        const token = await getAccessTokenSilently();
        const response = await trackedFetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/x-ndjson",
          },
          signal: abortController.signal,
        });
        if (!response.ok) {
          throw new Error(`Request failed (${response.status})`);
        }
        if (!response.body) {
          throw new Error("No response body to stream");
        }

        setIsLoading(false);
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let bufferedText = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          bufferedText += decoder.decode(value, { stream: true });
          const lines = bufferedText.split("\n");
          bufferedText = lines.pop() ?? "";
          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            bufferedRowsRef.current.push(JSON.parse(trimmed) as T);
            localCount += 1;
          }
          scheduleFlush();
        }

        bufferedText += decoder.decode();
        const finalLine = bufferedText.trim();
        if (finalLine) {
          bufferedRowsRef.current.push(JSON.parse(finalLine) as T);
          localCount += 1;
          scheduleFlush();
        }
        completedWithoutAbort = !abortController.signal.aborted;
      } catch (err) {
        if (!abortController.signal.aborted) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (flushTimerRef.current !== null) {
          clearTimeout(flushTimerRef.current);
          flushBufferedRows();
        }
        setIsLoading(false);
        setIsStreaming(false);
        if (staleWhileRevalidate && completedWithoutAbort) {
          setRows(accumulatedRows);
          setTotalReceived(localCount);
        }
        if (completedWithoutAbort && !abortController.signal.aborted) {
          streamCompletedCache.set(url, {
            rows: accumulatedRows.slice() as JsonRecord[],
            totalReceived: localCount,
            completedAt: Date.now(),
          });
        }
      }
    })();

    return () => {
      abortController.abort();
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
      }
    };
  }, [enabled, getAccessTokenSilently, restartVersion, url]);

  const restart = useCallback(() => {
    streamCompletedCache.delete(url);
    setRows([]);
    setTotalReceived(0);
    setRestartVersion((prev) => prev + 1);
  }, [url]);

  /** Refetch while keeping the current rows on screen. */
  const refresh = useCallback(() => {
    const cached = streamCompletedCache.get(url);
    if (cached) {
      cached.completedAt = 0;
    }
    setRestartVersion((prev) => prev + 1);
  }, [url]);

  useEffect(() => {
    return registerStreamRestarter(url, refresh);
  }, [refresh, url]);

  const invalidationGenerationRef = useRef(streamInvalidationGeneration);

  useEffect(() => {
    if (!enabled) return;
    if (invalidationGenerationRef.current === streamInvalidationGeneration) {
      return;
    }
    invalidationGenerationRef.current = streamInvalidationGeneration;
    refresh();
  }, [enabled, refresh]);

  return { rows, isLoading, isStreaming, error, totalReceived, restart };
}
